import * as React from 'react'
import { Link } from 'react-router-dom'
import { graphHighlightHref } from '@atbash-labs/plantview-links'
import type { NodeLabel } from '@atbash-labs/ontology-core'
import {
  collectAdditionalProperties,
  useGetPlantPartDetailQuery,
  usePlantSessionPaint,
  type PlantEquipment,
  type PlantLine,
  type PlantPartChild,
  type PlantPartLocation,
  type PlantPartProcedure,
  type PlantPartUsedIn,
  type PlantPartVendor,
  type PlantSite
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { plantEquipmentIndexHref } from '../../../lib/plant-links'
import {
  readBomAssemblyPath,
  readBomOptional,
  readBomQuantity
} from '../equipment-relationships/bom-tree'
import { FaultKnowledgeSection } from '../equipment-relationships/FaultKnowledgeSection'
import { PlantHref } from '../PlantHref'
import { registryIconFor } from '../registry-icons'
import {
  SessionBreadcrumbCurrent,
  SessionEntityMark,
  SessionTitleMark,
  sessionDetailHeaderClass
} from '../session-mark-ui'
import { EntityDetailsInspector } from './EntityDetailsInspector'
import { partSpareBadge, reconcileVendorChips } from './part-detail-helpers'
import { PlantStrayPill } from './PlantStrayPill'
import { SectionHeader } from './SectionHeader'
import { registeredLabel } from './entity-surface'

export function PartEntityPage({
  entityId
}: {
  entityId: string
  label: NodeLabel
}): React.ReactElement {
  const { data, isLoading, isError } = useGetPlantPartDetailQuery(entityId, {
    skip: !entityId
  })
  const { markFor } = usePlantSessionPaint()

  if (isLoading) {
    return (
      <div className="flex min-w-0 flex-col gap-card">
        <PartFactoryBreadcrumb />
        <div className="rounded-xl border bg-card p-8 text-sm text-ink-3">Loading part…</div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex min-w-0 flex-col gap-card">
        <PartFactoryBreadcrumb />
        <div className="rounded-xl border bg-card p-8 text-card-foreground shadow-sm">
          <h1 className="m-0 text-xl font-semibold">Part not found</h1>
          <p className="mt-2 break-all font-mono text-xs text-ink-3">{entityId}</p>
          <Button asChild variant="outline" className="mt-5">
            <Link to={graphHighlightHref(entityId)}>Open graph view</Link>
          </Button>
        </div>
      </div>
    )
  }

  const { part, usedIn, subParts, vendors, procedures, faults, locations, equipment, line, site } =
    data
  const Icon = registryIconFor('Part')
  const mark = markFor(part.id)
  const spareBadge = partSpareBadge(part)
  const legacyVendor = reconcileVendorChips(part.vendor, vendors)
  const additionalProperties = collectAdditionalProperties(
    'Part',
    part as unknown as Record<string, unknown>
  )
  const meta = [part.customer_pn, part.manufacturer_pn, part.manufacturer_name].filter(
    (value): value is string => typeof value === 'string' && value.length > 0
  )

  return (
    <div className="flex min-w-0 flex-col gap-card">
      <PartFactoryBreadcrumb
        site={site}
        line={line}
        equipment={equipment}
        currentId={part.id}
        currentName={part.name}
      />

      <header
        className={sessionDetailHeaderClass(
          'flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4',
          mark
        )}
      >
        <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
          <Icon size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">{part.name}</h1>
            <SessionTitleMark mark={mark} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge variant="outline">Part</Badge>
            {part.part_type && <Badge variant="secondary">{part.part_type}</Badge>}
            {spareBadge && (
              <Badge variant="outline" className={spareBadge.className}>
                {spareBadge.label}
              </Badge>
            )}
            {part.bin_location && <Badge variant="outline">Bin {part.bin_location}</Badge>}
            {part.unit_of_measure && <Badge variant="outline">UOM {part.unit_of_measure}</Badge>}
            <PlantStrayPill isStray={data.isStray} />
          </div>
          {meta.length > 0 && <div className="mt-1.5 text-13 text-ink-3">{meta.join(' · ')}</div>}
          <div className="mt-1.5 break-all font-mono text-11 text-ink-4">{part.id}</div>
        </div>
      </header>

      {part.description && (
        <p className="m-0 text-14 leading-relaxed text-ink-2">{part.description}</p>
      )}

      <UsedInEquipmentSection
        rows={usedIn.filter((row) => row.ownerLabels.includes('Equipment'))}
      />
      <UsedInParentPartsSection
        rows={usedIn.filter(
          (row) => row.ownerLabels.includes('Part') && !row.ownerLabels.includes('Equipment')
        )}
      />
      <SubPartsSection rows={subParts} />
      <VendorsSection rows={vendors} legacyVendor={legacyVendor} />
      <ProceduresSection rows={procedures} />
      <FaultKnowledgeSection faults={faults} aboutId={part.id} />
      <LocationsSection locations={locations} />
      <EntityDetailsInspector entity={part} additionalProperties={additionalProperties} />
    </div>
  )
}

function PartFactoryBreadcrumb({
  site,
  line,
  equipment,
  currentId,
  currentName
}: {
  site?: PlantSite | null
  line?: PlantLine | null
  equipment?: PlantEquipment | null
  currentId?: string
  currentName?: string
}): React.ReactElement {
  return (
    <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
      <Link to={plantEquipmentIndexHref()} className="hover:text-ink hover:underline">
        Sites
      </Link>
      {site && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={site.id}
            label="Site"
            node={site}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {site.name}
            <SessionEntityMark entityId={site.id} />
          </PlantHref>
        </>
      )}
      {line && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={line.id}
            label="Line"
            node={site ? { ...line, siteId: site.id } : line}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {line.name}
            <SessionEntityMark entityId={line.id} />
          </PlantHref>
        </>
      )}
      {equipment && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <PlantHref
            id={equipment.id}
            label="Equipment"
            node={equipment}
            className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
          >
            {equipment.name}
            <SessionEntityMark entityId={equipment.id} />
          </PlantHref>
        </>
      )}
      {currentId && currentName && (
        <>
          <span className="mx-1.5 text-ink-4">/</span>
          <SessionBreadcrumbCurrent entityId={currentId}>{currentName}</SessionBreadcrumbCurrent>
        </>
      )}
    </nav>
  )
}

function UsedInEquipmentSection({ rows }: { rows: PlantPartUsedIn[] }): React.ReactElement | null {
  if (rows.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Used in equipment"
        subtitle={`${rows.length} ${rows.length === 1 ? 'asset' : 'assets'}`}
      />
      <div className="flex flex-wrap gap-2 rounded-xl border bg-card p-4 shadow-sm">
        {rows.map((row) => {
          const quantity = readBomQuantity(row.edge)
          const assemblyPath = readBomAssemblyPath(row.edge)
          const optional = readBomOptional(row.edge)
          return (
            <PlantHref
              key={row.owner.id}
              id={row.owner.id}
              label="Equipment"
              node={row.owner}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border bg-bg-elev px-2.5 py-1 text-12 font-medium text-ink no-underline hover:border-brand/40 hover:text-brand"
            >
              <span className="min-w-0 truncate">{row.owner.name}</span>
              {quantity !== undefined && (
                <span className="text-11 font-normal text-ink-3">×{quantity}</span>
              )}
              {assemblyPath && (
                <span className="font-mono text-10 font-normal text-ink-3">{assemblyPath}</span>
              )}
              {optional && (
                <Badge variant="outline" className="px-1.5 py-0 text-9">
                  optional
                </Badge>
              )}
              <SessionEntityMark entityId={row.owner.id} />
            </PlantHref>
          )
        })}
      </div>
    </section>
  )
}

function UsedInParentPartsSection({
  rows
}: {
  rows: PlantPartUsedIn[]
}): React.ReactElement | null {
  if (rows.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Used in assemblies"
        subtitle={`${rows.length} ${rows.length === 1 ? 'part' : 'parts'}`}
      />
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-12">
            <thead>
              <tr className="border-b text-10 uppercase tracking-[0.04em] text-ink-3">
                <th className="px-3 py-1.5 font-semibold">Parent part</th>
                <th className="px-3 py-1.5 font-semibold">Qty</th>
                <th className="px-3 py-1.5 font-semibold">Assembly path</th>
                <th className="px-3 py-1.5 font-semibold">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const quantity = readBomQuantity(row.edge)
                const assemblyPath = readBomAssemblyPath(row.edge)
                const optional = readBomOptional(row.edge)
                return (
                  <tr key={row.owner.id} className="border-b last:border-b-0">
                    <td className="px-3 py-1.5 align-top">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <PlantHref
                          id={row.owner.id}
                          label="Part"
                          node={row.owner}
                          className="min-w-0 truncate font-medium text-ink no-underline hover:text-brand hover:underline"
                        >
                          {row.owner.name}
                        </PlantHref>
                        <SessionEntityMark entityId={row.owner.id} />
                      </div>
                    </td>
                    <td className="px-3 py-1.5 font-mono tabular-nums text-ink-2">
                      {quantity !== undefined ? `×${quantity}` : '—'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-ink-3">{assemblyPath ?? '—'}</td>
                    <td className="px-3 py-1.5">
                      {optional ? (
                        <Badge variant="outline" className="px-1.5 py-0 text-10">
                          optional
                        </Badge>
                      ) : (
                        <span className="text-ink-4">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

function SubPartsSection({ rows }: { rows: PlantPartChild[] }): React.ReactElement | null {
  if (rows.length === 0) return null
  return (
    <section>
      <SectionHeader label="Sub-parts" subtitle={`${rows.length} parts`} />
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-12">
            <thead>
              <tr className="border-b text-10 uppercase tracking-[0.04em] text-ink-3">
                <th className="px-3 py-1.5 font-semibold">Part</th>
                <th className="px-3 py-1.5 font-semibold">Qty</th>
                <th className="px-3 py-1.5 font-semibold">Assembly path</th>
                <th className="px-3 py-1.5 font-semibold">P/Ns</th>
                <th className="px-3 py-1.5 font-semibold">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ part, edge }) => {
                const quantity = readBomQuantity(edge)
                const assemblyPath = readBomAssemblyPath(edge)
                const optional = readBomOptional(edge)
                const pnBits = [part.customer_pn, part.manufacturer_pn].filter(
                  (value): value is string => typeof value === 'string' && value.length > 0
                )
                const spareBadge = partSpareBadge(part)
                return (
                  <tr key={part.id} className="border-b last:border-b-0">
                    <td className="px-3 py-1.5 align-top">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <PlantHref
                          id={part.id}
                          label="Part"
                          node={part}
                          className="min-w-0 truncate font-medium text-ink no-underline hover:text-brand hover:underline"
                        >
                          {part.name}
                        </PlantHref>
                        <SessionEntityMark entityId={part.id} />
                      </div>
                    </td>
                    <td className="px-3 py-1.5 font-mono tabular-nums text-ink-2">
                      {quantity !== undefined ? `×${quantity}` : '—'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-ink-3">{assemblyPath ?? '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-ink-3">
                      {pnBits.length > 0 ? pnBits.join(' · ') : '—'}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {optional && (
                          <Badge variant="outline" className="px-1.5 py-0 text-10">
                            optional
                          </Badge>
                        )}
                        {spareBadge && (
                          <Badge
                            variant="outline"
                            className={`px-1.5 py-0 text-10 ${spareBadge.className}`}
                          >
                            {spareBadge.label}
                          </Badge>
                        )}
                        {!optional && !spareBadge && <span className="text-ink-4">—</span>}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

function VendorsSection({
  rows,
  legacyVendor
}: {
  rows: PlantPartVendor[]
  legacyVendor: string | null
}): React.ReactElement | null {
  if (rows.length === 0 && !legacyVendor) return null
  return (
    <section>
      <SectionHeader label="Vendors" subtitle={`${rows.length} linked`} />
      <div className="flex flex-wrap gap-2 rounded-xl border bg-card p-4 shadow-sm">
        {rows.map(({ vendor, role }) => (
          <PlantHref
            key={`${vendor.id}:${role}`}
            id={vendor.id}
            label="Vendor"
            node={vendor}
            className="inline-flex items-center gap-1.5 rounded-full border bg-bg-elev px-2.5 py-1 text-12 font-medium text-ink no-underline hover:border-brand/40 hover:text-brand"
          >
            {vendor.name}
            <Badge variant="secondary" className="px-1.5 py-0 text-9 capitalize">
              {role}
            </Badge>
            <SessionEntityMark entityId={vendor.id} />
          </PlantHref>
        ))}
        {legacyVendor && (
          <Badge variant="outline" className="px-2.5 py-1 text-12">
            {legacyVendor}
          </Badge>
        )}
      </div>
    </section>
  )
}

function ProceduresSection({ rows }: { rows: PlantPartProcedure[] }): React.ReactElement | null {
  if (rows.length === 0) return null
  return (
    <section>
      <SectionHeader label="Required by procedures" subtitle={`${rows.length} procedures`} />
      <div className="flex flex-col gap-2 rounded-xl border bg-card p-4 shadow-sm">
        {rows.map(({ procedure, quantity, isOptional }) => (
          <div key={procedure.id} className="flex min-w-0 flex-wrap items-center gap-2">
            <PlantHref
              id={procedure.id}
              label="Procedure"
              node={procedure}
              className="min-w-0 text-13 font-medium text-ink no-underline hover:text-brand hover:underline"
            >
              {procedure.name}
            </PlantHref>
            {quantity !== undefined && <span className="text-12 text-ink-3">×{quantity}</span>}
            {isOptional && (
              <Badge variant="outline" className="text-10">
                optional
              </Badge>
            )}
            <span className="ml-auto">
              <SessionEntityMark entityId={procedure.id} />
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}

function LocationsSection({
  locations
}: {
  locations: PlantPartLocation[]
}): React.ReactElement | null {
  if (locations.length === 0) return null
  return (
    <section>
      <SectionHeader label="Location" subtitle={`${locations.length} locations`} />
      <div className="flex flex-wrap gap-2 rounded-xl border bg-card p-4 shadow-sm">
        {locations.map(({ location, locationLabels }) => {
          const label = registeredLabel(locationLabels)
          return (
            <PlantHref
              key={location.id}
              id={location.id}
              label={label ?? undefined}
              node={location}
              className="inline-flex items-center gap-1.5 rounded-full border bg-bg-elev px-2.5 py-1 text-12 font-medium text-ink no-underline hover:border-brand/40 hover:text-brand"
            >
              {location.name}
              <SessionEntityMark entityId={location.id} />
            </PlantHref>
          )
        })}
      </div>
    </section>
  )
}
